import {
  Dialog,
  DialogProps,
  dialogTitleClasses,
  SwipeableDrawer,
  SwipeableDrawerProps,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { usePreventScroll } from '@react-aria/overlays';
import { ReactNode, useState } from 'react';

import { isiOSDevice } from 'utils/browser.utils';

import DrawerActions from './DrawerActions';
import { DrawerScrollContainer } from './ResponsiveDrawerDialog.styles';

interface Props {
  isOpen: boolean;
  children: ReactNode;
  dialogProps?: Omit<DialogProps, 'open'>;
  drawerProps?: Omit<SwipeableDrawerProps, 'open' | 'onClose' | 'onOpen'>;
  stackPriority?: number;
  onClose?: () => void;
  onBack?: () => void;
}

const ResponsiveDrawerDialog = ({
  isOpen,
  children,
  dialogProps,
  drawerProps,
  stackPriority = 0,
  onClose,
  onBack,
}: Props) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  // Or not mobile to have Cover image sticky
  usePreventScroll({ isDisabled: !isOpen || !isMobile });

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onClose={onClose ?? (() => {})}
        // Not using this
        onOpen={() => {}}
        ModalProps={{
          sx: (theme) => ({
            // Ensures we can stack the drawer above a modal
            zIndex: theme.zIndex.modal + stackPriority,
          }),
        }}
        PaperProps={{
          sx: (theme) => ({
            height: 'auto',
            maxHeight: 'calc(100% - 32px)',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            overflow: 'hidden',
            borderTop: `1px solid ${theme.vars.palette.grey[800]}`,
            // Match absolute chrome (physical left/right). Only reserve end space when close exists.
            [`& .${dialogTitleClasses.root}`]: {
              paddingBlock: theme.spacing(3),
              paddingInline: theme.spacing(2),
              ...(onBack && {
                paddingLeft: theme.spacing(10),
              }),
              ...(onClose && {
                paddingRight: theme.spacing(9),
              }),
            },
          }),
        }}
        disableBackdropTransition={!isiOSDevice()}
        disableDiscovery={isiOSDevice()}
        disableSwipeToOpen
        onTransitionEnter={() => setIsTransitioning(true)}
        onTransitionExited={() => setIsTransitioning(false)}
        {...drawerProps}
      >
        {/* Only render children when needed to optimize performance */}
        {(isOpen || isTransitioning) && (
          <DrawerScrollContainer>
            <DrawerActions onClose={onClose} onBack={onBack} />
            {children}
          </DrawerScrollContainer>
        )}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      sx={(theme) => ({
        zIndex: theme.zIndex.modal + stackPriority,
        // Match absolute chrome (physical left/right). Only reserve end space when close exists.
        [`& .${dialogTitleClasses.root}`]: {
          paddingBlock: theme.spacing(3),
          paddingInline: theme.spacing(3),
          ...(onBack && {
            paddingLeft: theme.spacing(12),
          }),
          ...(onClose && {
            paddingRight: theme.spacing(10),
          }),
        },
      })}
      {...dialogProps}
    >
      {isOpen && (
        <>
          <DrawerActions onClose={onClose} onBack={onBack} />
          {children}
        </>
      )}
    </Dialog>
  );
};

export default ResponsiveDrawerDialog;
